import { useEffect, useState } from 'react'
import { LAST_PCT_KEY, ANIM_DURATION_MS } from '@/common/navigation/animation'

function safeGetItem(key: string): string | null {
    try {
        if (typeof window === 'undefined' || !window.localStorage) return null
        return window.localStorage.getItem(key)
    } catch {
        return null
    }
}

function safeSetItem(key: string, value: string) {
    try {
        if (typeof window === 'undefined' || !window.localStorage) return
        window.localStorage.setItem(key, value)
    } catch {
        // ignore errors
    }
}

export default function usePersistentProgressAnimation(computedTargetPct: number, isLoading: boolean) {
    const [displayPct, setDisplayPct] = useState<number>(0)
    const [animateBg, setAnimateBg] = useState<boolean>(false)

    useEffect(() => {
        // initialize from stored value if present
        const raw = safeGetItem(LAST_PCT_KEY)
        const stored = raw ? Math.max(0, Math.min(100, parseInt(raw, 10) || 0)) : 0
        if (stored > 0) {
            setDisplayPct(stored)
            setAnimateBg(true)
        }
    }, [])

    useEffect(() => {
        if (isLoading) return

        const prefersReduced =
            typeof window !== 'undefined' && typeof window.matchMedia === 'function'
                ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
                : false

        let stored = 0
        const raw = safeGetItem(LAST_PCT_KEY)
        stored = raw ? Math.max(0, Math.min(100, parseInt(raw, 10) || 0)) : 0

        // If the user prefers reduced motion, skip animation and set final state
        if (prefersReduced) {
            setDisplayPct(computedTargetPct)
            setAnimateBg(true)
            safeSetItem(LAST_PCT_KEY, String(computedTargetPct))
            return
        }

        // If progress increased since last stored value, animate from stored -> computed
        if (computedTargetPct > stored) {
            setDisplayPct(stored)
            setAnimateBg(false)

            let rafId: number | null = null
            let timeoutId: number | null = null

            rafId = requestAnimationFrame(() => {
                setDisplayPct(computedTargetPct)
                requestAnimationFrame(() => setAnimateBg(true))

                timeoutId = window.setTimeout(() => {
                    safeSetItem(LAST_PCT_KEY, String(computedTargetPct))
                }, ANIM_DURATION_MS)
            })

            return () => {
                if (rafId !== null) cancelAnimationFrame(rafId)
                if (timeoutId !== null) clearTimeout(timeoutId)
            }
        }

        // No increase: set to computed immediately and persist current value
        setDisplayPct(computedTargetPct)
        setAnimateBg(true)
        safeSetItem(LAST_PCT_KEY, String(computedTargetPct))
    }, [isLoading, computedTargetPct])

    return { displayPct, animateBg }
}
